import { showNotification } from "@/plugins/notification";

import router from "@/router/index.js";
import { getUserFriendlyErrorMessage } from '@/utils/errorHandler.js';

export default {
  async getMe({ commit }) {
    if (!commit) return;

    try {
      let response = await fetch(`/api/users/me`);

      if (response.status === 403 || response.status === 401) {
        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          console.warn('Failed to parse error response:', jsonError);
          data = {};
        }

        if (
          data?.message === "User account is deleted" ||
          data?.message === "User no longer exists"
        ) {
          await fetch("/api/auth/logout", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          });

          localStorage.removeItem("user");
          localStorage.removeItem("activeTab");
          commit("setAuthStatus", false);
          window.location.href = "/";
          return;
        }

        // For other 401/403 errors, don't try to parse response again
        localStorage.removeItem("user");
        commit("setAuthStatus", false);
        return null;
      }

      // Only parse response if it's not an error status
      let profile;
      try {
        profile = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse user profile response:', jsonError);
        throw new Error('Invalid response format');
      }

      if (!profile.weighInThresholds?.[0]) {
        let arr = [1, 2, 3, 4, 5];
        arr.forEach((item) => {
          profile.weighInThresholds?.push({ threshold: "off" });
        });
      }

      if (profile.statusCode === 401 || profile.statusCode === 403) {
        localStorage.removeItem("user");
        commit("setAuthStatus", false);
      } else {
        localStorage.setItem("user", JSON.stringify(profile));
        commit("setUserData", profile);
        commit("setAuthStatus", true);
      }
      return profile;
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      console.error('Authentication error:', friendlyMessage);

      // In development, also log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Original auth error:', err);
      }
      commit("setAuthStatus", false);
      return null;
    }
  },
  signUp({ commit }, { phone, password }) {
    try {
      fetch(`/api/users/signup`, {
        method: "POST",
        body: JSON.stringify({
          phone,
          password,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then(async (data) => {
          if (data.error) {
            console.log("error", data.message);
          } else {
            if (!data.id || data.id === "null") {
              console.error("User ID is required", new Error().stack);
              return;
            }
            let response = await fetch(`/api/users/${data.id}`);
            let profile = await response.json();
            localStorage.setItem("user", JSON.stringify(profile));
            commit("setUserData", profile);
            commit("setAuthStatus", true);
            router.push("/");
          }
        });
    } catch (error) {
      console.log("error", error);
    }
  },
  logIn({ commit }, { phone, password }) {
    try {
      fetch(`/api/auth/login`, {
        method: "POST",
        body: JSON.stringify({
          phone,
          password,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then(async (data) => {
          if (data.error) {
            console.log("error", data.message);
          } else {
            if (!data.satusCode) {
              commit("setAuthStatus", true);
              if (!data.id || data.id === "null") {
                console.error("User ID is required", new Error().stack);
                router.push("/onboarding/login-method");
              }
              let response = await fetch(`/api/users/${data.id}`);
              let profile = await response.json();
              if (!profile.weighInThresholds[0]) {
                let arr = [1, 2, 3, 4, 5];
                arr.forEach((item) => {
                  profile.weighInThresholds.push({ threshold: "off" });
                });
              }
              localStorage.setItem("user", JSON.stringify(profile));
              commit("setUserData", profile);
              router.push("/");
            } else {
              commit("setUserDataError", data);
            }
          }
        });
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      console.log("Authentication error:", friendlyMessage);
      commit("setUserDataError", { message: friendlyMessage });

      // In development, also log the original error for debugging
      if (import.meta.env.MODE === 'development') {
        console.error('Original error:', error);
      }
    }
  },
  async logOut({ commit }, error) {
    let response = await fetch(`/api/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (response.ok || response.status === 403) {
      commit("setAuthStatus", false);
      let data = null;
      localStorage.removeItem("activeTab");
      localStorage.removeItem("user");
      localStorage.removeItem("isFirstLogin");
      commit("setProfile", data);
      commit("setUserData", data);
      // router.push("/welcome");
      //  router.push("/sign-in");
      window.location.replace("/sign-in");
    }
  },

  async sendSmsCode({ commit }, { phone, captchaToken, type }) {
    try {
      const response = await fetch(`/api/auth/login-sms/send`, {
        method: "POST",
        body: JSON.stringify({ phone, type }),
        headers: {
          "Content-Type": "application/json",
          "X-Captcha-Token": captchaToken || "",
        },
      });
      if (response.status === 204) {
        commit("setShowSmsCodeInput", true);
        return {
          message: "Success",
          status: "success",
        };
      } else {
        const data = await response.json();
        console.log("error", data.message);
        return {
          message: data.message,
          status: "error",
        };
      }
    } catch (error) {
      console.log("error", error);
      return {
        message: "Server error",
        status: "error",
      };
    }
  },
  async verifySmsCode(
    { commit },
    { phone, code, username, firstName, lastName, email }
  ) {
    try {
      const requestData = {
        phone,
        code,
        ...(username && { username }),
        ...(firstName && { firstName }),
        ...(lastName && { lastName }),
        ...(email && { email }),
      };
      const response = await fetch(`/api/auth/login-sms/check`, {
        method: "POST",
        body: JSON.stringify(requestData),
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
     if (data?.twoFaRequired) {
      return {
        status: "twofa",
        tempToken: data.tempToken,
        userId: data.userId,
        message: data.message,
      };
    }
      if (data.statusCode === 409) {
        commit("setUserDataError", data.message);
        return {
          status: "error",
          usernameTaken: true,
          message: data.message,
        };
      }
      if (data.error) {
        console.log("error", data.message);
        commit("setUserDataError", data.message);
        return {
          status: "error",
          message: data.message,
        };
      }
      if (!data.statusCode) {
        if (!data.id || data.id === "null") {
          console.error("User ID is required", new Error().stack);
          return;
        }
        let response = await fetch(`/api/users/${data.id}`);
        let profile = await response.json();
        if (!profile.weighInThresholds[0]) {
          let arr = [1, 2, 3, 4, 5];
          arr.forEach((item) => {
            profile.weighInThresholds.push({ threshold: "off" });
          });
        }
        localStorage.setItem("user", JSON.stringify(profile));
        localStorage.removeItem("userPhoneX");
        commit("setUserData", profile);
        commit("setAuthStatus", true);
        return {
          status: "success",
          message: "",
          profile,
        };
      } else {
        commit("setUserDataError", data);
        return {
          status: "error",
          message: data.message,
        };
      }
    } catch (error) {
      console.log("error", error);
      commit("setUserDataError", error.message);
      return {
        status: "error",
        message: error.message,
      };
    }
  },
  //api for two facto...
  // ✅ VERIFY 2FA LOGIN
async verifyTwoFaLogin({ commit }, { userId, code }) {
  try {
    const requestData = {
      userId,
      code,
    };

    const response = await fetch(`/api/auth/2fa/verify-login`, {
      method: "POST",
      body: JSON.stringify(requestData),
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // allows backend cookie (JWT) to be set
    });

    const data = await response.json();

    // Invalid 2FA code
    if (!response.ok || data.error) {
      commit("setUserDataError", data.message || "Invalid 2FA code");
      return {
        status: "error",
        message: data.message || "Invalid 2FA code",
      };
    }

    // Successful 2FA verification → get user profile
    if (data.id) {
      const userRes = await fetch(`/api/users/${data.id}`);
      const profile = await userRes.json();

      // Set defaults like your existing login
      if (!profile.weighInThresholds[0]) {
        let arr = [1, 2, 3, 4, 5];
        arr.forEach((item) => {
          profile.weighInThresholds.push({ threshold: "off" });
        });
      }

      // Save in localStorage and Vuex
      localStorage.setItem("user", JSON.stringify(profile));
      console.error(" JSON.stringify(profile)", JSON.stringify(profile));
      commit("setUserData", profile);
      commit("setAuthStatus", true);

      return {
        status: "success",
        message: "2FA verified successfully",
        profile,
      };
    } else {
      return {
        status: "error",
        message: data.message || "Something went wrong",
      };
    }
  } catch (error) {
    console.error("2FA verification failed:", error);
    commit("setUserDataError", error.message);
    return {
      status: "error",
      message: error.message,
    };
  }
},

  async logInWithX({ commit }, { oauthToken, oauthVerifier }) {
    try {
      let response = await fetch(`/api/users/connect-x`, {
        method: "POST",
        body: JSON.stringify({
          oauthToken,
          oauthVerifier,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) {
        console.log("error", data.message);
        localStorage.removeItem("oauthToken");
        localStorage.removeItem("oauthVerifier");
  localStorage.setItem("isPostToX", String(false));
        commit("setUserDataError", data.message);
        return {
          status: "error",
          message: data.message,
        };
      }
      if (!data.statusCode) {
        if (!data.id || data.id === "null") {
          console.error("User ID is required", new Error().stack);
          return;
        }
        let response = await fetch(`/api/users/${data.id}`);
        let profile = await response.json();
        if (!profile.weighInThresholds[0]) {
          let arr = [1, 2, 3, 4, 5];
          arr.forEach((item) => {
            profile.weighInThresholds.push({ threshold: "off" });
          });
        }
        localStorage.setItem("user", JSON.stringify(profile));
        localStorage.removeItem("oauthToken");
        localStorage.removeItem("oauthVerifier");
  localStorage.setItem("isPostToX", String(true));
        router.push("/");
        showNotification(
          "success",
          `X Account Connected!`,
          "Your X account has been successfully connected. Your posts will now be automatically published to X.",
          7000
        );
        commit("setUserData", profile);
        return {
          status: "success",
          message: "",
        };
      } else {
        commit("setUserDataError", data);
        return {
          status: "error",
          message: data.message,
        };
      }
    } catch (error) {
      console.log("error", error);
      commit("setUserDataError", error.message);
      return {
        status: "error",
        message: error.message,
      };
    }
  },

  async fetchDisconnectX({ commit }) {
    try {
      let response = await fetch(`/api/users/disconnect-x`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (!data.statusCode) {
        if (!data.id || data.id === "null") {
          console.error("User ID is required", new Error().stack);
          return;
        }
        let response = await fetch(`/api/users/${data.id}`);
        let profile = await response.json();
        if (!profile.weighInThresholds[0]) {
          let arr = [1, 2, 3, 4, 5];
          arr.forEach((item) => {
            profile.weighInThresholds.push({ threshold: "off" });
          });
        }
        localStorage.setItem("user", JSON.stringify(profile));
        showNotification(
          "success",
          `X Account Disconnected`,
          "Your X account has been disconnected. Posts will no longer be published to X.",
          7000
        );
        commit("setUserData", profile);
        return data;
      } else {
        commit("setUserDataError", data);
        return data;
      }
    } catch (error) {
      console.log("error", error);
      commit("setUserDataError", error.message);
    }
  },

  async addAuthStatus({ commit }, status) {
    commit("setAuthStatus", status);
  },
  async authWithX({ commit }) {
    try {
      const response = await fetch(`/api/auth/login-x/request-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) {
        console.log("error", data.message);
        commit("setUserDataError", data.message);
      }
      if (!data.statusCode) {
        document.location.href = `https://api.x.com/oauth/authenticate?oauth_token=${data.oauthToken}`;
      } else {
        commit("setUserDataError", data);
      }
    } catch (error) {}
  },
  async deleteCurrentUser({ commit }, { reason }) {
    try {
      const response = await fetch(`/api/users/me`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: reason ? JSON.stringify({ reason }) : undefined,
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error("error:", errorData.message);
        throw new Error(errorData.message);
      }
      console.log("User deletion successful");
    } catch (error) {
      console.log("error", error);
      throw error;
    }
  },

  resetShowSmsCodeInput({ commit }) {
    commit("setShowSmsCodeInput", false);
  },

  async siwfCallback({ commit }, param) {
    try {
      let response = await fetch(
        `/api/siwf/callback?authorizationCode=${param}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 403 || response.status === 401) {
        const data = await response.json().catch(() => ({}));
        if (
          data?.message === "User account is deleted" ||
          data?.message === "User no longer exists" ||
          data?.message === "Unauthorized"
        ) {
          await fetch("/api/auth/logout", {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          });

          localStorage.removeItem("user");
          localStorage.removeItem("activeTab");
          commit("setAuthStatus", false);
          router.push({
            path: "/siwf/register",
          });
          return;
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      let data = await response.json();

      // Check if the response indicates success
      if (response.status === 200 && (!data.statusCode || data.statusCode === 200)) {
        commit("setAuthStatus", true);

        if (data.is_new_user) {
          // Store all user data for new user registration
          localStorage.setItem("newUser", JSON.stringify(data));
          router.push({
            path: "/siwf/register",
          });
        } else {
          // Existing user flow
          try {
            let userResponse = await fetch(
              `/api/users/${data.user._id || data.user.id}`
            );

            let profile = await userResponse.json();
            if (userResponse.status === 404) {
              if (profile.message === "User not found") {
                showNotification(
                  "info",
                  `Info`,
                  `User not found. Please Signup`,
                  7000
                );
                router.push({
                  path: "/siwf/register",
                });
                return;
              }
            }

            // Ensure weighInThresholds exist
          if (!profile.weighInThresholds || !profile.weighInThresholds.length) {
              profile.weighInThresholds = [];
              let arr = [1, 2, 3, 4, 5];
              arr.forEach((item) => {
                profile.weighInThresholds.push({ threshold: "off" });
              });
            }

            localStorage.setItem("user", JSON.stringify(profile));
            commit("setUserData", profile);

            // Initialize app services after successful authentication
            try {
              // Start global services
            this.dispatch('startGlobalTimer');
            this.dispatch('fetchReputationCategories');
            this.dispatch('fetchUnreadNotificationsCount');
            console.log('SIWF: Successfully initialized app services');
            } catch (serviceError) {
            console.warn('SIWF: Some services failed to initialize:', serviceError);
            }

            router.push("/");
          } catch (error) {
            console.error(error);
            throw new Error(`Authentication failed. Please try again.`);
          }
        }
      } else {
        // Handle error responses
        commit("setUserDataError", data);
        console.error('SIWF callback error:', data);
      }
    } catch (error) {
      console.error('SIWF callback failed:', error);
      commit("setUserDataError", { message: error.message });
      // Optionally redirect to an error page or show notification
      showNotification('error', 'Error', 'Authentication failed. Please try again.', 7000);

    }
  },
};
